import { CustomRoute } from "@/interfaces";
import {
  CheckCircle,
  CircleDollarSign,
  CreditCard,
  FileText,
  LayoutDashboard,
  Percent,
  Receipt,
  Repeat,
  User,
} from "lucide-react";
import React from "react";

// Lazy load Finance module components
const Dashboard = React.lazy(() => import("../modules/Finance/Dashboard"));
const CreateInvoice = React.lazy(() => import("../modules/Finance/CreateInvoice"));
const EditProfile = React.lazy(() => import("../modules/Profile/EditProfile"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));
const InvoiceManagement = React.lazy(() => import("../modules/Finance/InvoiceManagement"));
const ApprovalManagement = React.lazy(() => import("../modules/Finance/ApprovalManagement"));
const AllCommissions = React.lazy(() => import("../modules/Finance/CommissionManagement/AllCommissions"));
const CommissionPayouts = React.lazy(() => import("../modules/Finance/CommissionManagement/CommissionPayouts"));
const CommissionTemplates = React.lazy(() => import("../modules/Finance/CommissionManagement/CommissionTemplates"));
const StripePayment = React.lazy(() => import("../modules/Finance/StripePayment"));
const VendorBills = React.lazy(() => import("../modules/Finance/ListVendorBills/AllBills"));
const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
const CreateBill = React.lazy(() => import("../modules/Finance/CreateBill/CreateBill"));
const RecurringInvoices = React.lazy(() => import("../modules/Admin/Invoices/RecurringInvoices"));

/**
 * Finance user routes
 * Finance users see data across all companies without company switching
 * All tables include company column and company filter
 */
const financeRoutes: CustomRoute[] = [
  {
    path: "/",
    id: "Finance",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "QuickBooks",
    icon: CreditCard,
    highlightOn: ["quickbooks"],
    submenu: [
      {
        path: "/quickbooks/invoices",
        id: "Finance",
        element: <InvoiceManagement provider="quickbooks" />,
        menuName: "Invoices",
        icon: FileText,
        highlightOn: ["quickbooks-invoices"],
      },
      {
        path: "/quickbooks/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="quickbooks" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["quickbooks-approvals"],
      },
      {
        path: "/quickbooks/recurring-invoices",
        id: "Finance",
        element: <RecurringInvoices />,
        menuName: "Recurring Transactions",
        icon: Repeat,
        highlightOn: ["quickbooks-recurring-invoices"],
      },
    ],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "NetSuite",
    icon: CircleDollarSign,
    highlightOn: ["netsuite"],
    submenu: [
      {
        path: "/netsuite/invoices",
        id: "Finance",
        element: <InvoiceManagement provider="netsuite" />,
        menuName: "Invoices",
        icon: FileText,
        highlightOn: ["netsuite-invoices"],
      },
      {
        path: "/netsuite/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="netsuite" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-approvals"],
      },
      {
        path: "/netsuite/vendor-bills",
        id: "Finance",
        element: <VendorBills />,
        menuName: "Vendor Bills",
        icon: FileText,
        highlightOn: ["netsuite-vendor-bills"],
      },
      {
        path: "/netsuite/vendor-bill-approvals",
        id: "Finance",
        element: <VendorBillApprovals />,
        menuName: "Vendor Bills Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-vendor-bill-approvals"],
      },
    ],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "Commissions",
    icon: Percent,
    highlightOn: ["commissions"],
    submenu: [
      {
        path: "/commissions",
        id: "Finance",
        element: <AllCommissions />,
        menuName: "All Commissions",
        icon: FileText,
        highlightOn: ["all-commissions"],
      },
      {
        path: "/commissions/payouts",
        id: "Finance",
        element: <CommissionPayouts />,
        menuName: "Payouts",
        icon: Receipt,
        highlightOn: ["commission-payouts"],
      },
      {
        path: "/commissions/templates",
        id: "Finance",
        element: <CommissionTemplates />,
        menuName: "Templates",
        icon: FileText,
        highlightOn: ["commission-templates"],
      },
    ],
  },
  {
    path: "/payments",
    id: "Finance",
    element: <StripePayment />,
    menuName: "Stripe Payments",
    icon: CreditCard,
    highlightOn: ["payments"],
  },
  {
    path: "/create-invoice",
    id: "Finance",
    element: <CreateInvoice />,
    menuName: "Create Invoice",
    icon: FileText,
    highlightOn: ["create-invoice"],
  },
  {
    path: "/create-bill",
    id: "Finance",
    element: <CreateBill />,
    menuName: "Create Bill",
    icon: FileText,
    highlightOn: ["create-bill"],
  },
  {
    path: "/notifications",
    id: "Finance",
    element: <AllNotifications />,
    menuName: "Notifications",
    hideInMenu: true,
    highlightOn: ["notifications"],
  },
  {
    path: "/profile",
    id: "Finance",
    element: <EditProfile />,
    menuName: "Profile",
    icon: User,
    highlightOn: ["profile"],
  },
];

export default financeRoutes;
